import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import Permissions from '../../auth/Permissions';
import { getIdMap } from '../../helpers/helpers';
import { getAllDayValues } from '../../helpers/time';
import { getBackgroundColor } from '../scheduleHelpers';
import ScheduleShiftHoverCard from './ScheduleShiftHoverCard';

export default function MonthShiftHover({
  shift,
  children,
  placement = 'bottomLeft',
}) {
  const stateUsers = useSelector((state) => state.users.users);
  const categories = useSelector((state) => state.schedule.categories);
  const userMap = useMemo(() => getIdMap(stateUsers), [stateUsers]);
  const categoryMap = useMemo(() => getIdMap(categories || []), [categories]);

  if (!shift) return children;

  const {
    title,
    startTime,
    endTime,
    users = [],
    color,
    isEvent,
    isDraft,
    projectId,
    phaseId,
    costcodeId,
    shiftCategoryId,
    categoryId,
    timezone = DateTime.local().zoneName,
  } = shift;
  const isMyShift = users?.includes(Permissions.id);
  const backgroundColor = getBackgroundColor(users, userMap, color, isMyShift, isEvent, isDraft);
  const {
    [shiftCategoryId || categoryId]: {
      name: categoryName,
      color: categoryColor,
    } = {},
  } = categoryMap;
  const assignedUsers = (users || [])
    .map((userId) => userMap[userId] || {})
    .filter((user) => user.name);
  const startInShiftTimezone = startTime
    ? DateTime.fromMillis(startTime, { zone: timezone })
    : null;
  const endInShiftTimezone = endTime
    ? DateTime.fromMillis(endTime, { zone: timezone })
    : null;
  const { isAllDay } = getAllDayValues(startInShiftTimezone, endInShiftTimezone);

  return (
    <ScheduleShiftHoverCard
      placement={placement}
      title={title}
      color={backgroundColor}
      startTime={startTime}
      endTime={endTime}
      allDay={isAllDay}
      users={assignedUsers}
      isEvent={isEvent}
      isDraft={isDraft}
      projectId={projectId}
      phaseId={phaseId}
      costcodeId={costcodeId}
      categoryName={categoryName}
      categoryColor={categoryColor}
    >
      {children}
    </ScheduleShiftHoverCard>
  );
}

MonthShiftHover.propTypes = {
  shift: PropTypes.shape({
    title: PropTypes.string,
    startTime: PropTypes.number,
    endTime: PropTypes.number,
    users: PropTypes.arrayOf(PropTypes.string),
    color: PropTypes.string,
    isEvent: PropTypes.bool,
    isDraft: PropTypes.bool,
    projectId: PropTypes.string,
    phaseId: PropTypes.string,
    costcodeId: PropTypes.string,
    shiftCategoryId: PropTypes.string,
    categoryId: PropTypes.string,
    timezone: PropTypes.string,
  }),
  children: PropTypes.node.isRequired,
  placement: PropTypes.string,
};

MonthShiftHover.defaultProps = {
  shift: null,
  placement: 'bottomLeft',
};
